import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  ArrowLeft, Camera, Upload, Loader2, CheckCircle, 
  AlertCircle, ScanLine, ImagePlus, X, Package, ChevronDown, Check, Focus, HelpCircle, Search
} from "lucide-react";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import { useSmartCapture } from "@/hooks/useSmartCapture";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Video constraints with autofocus enabled for better close-up scanning
const VIDEO_CONSTRAINTS = {
  facingMode: "environment",
  width: { ideal: 1920 },
  height: { ideal: 1080 },
  focusMode: "continuous",
  // Advanced constraints for better focus
  advanced: [{ focusMode: "continuous" }]
};

// Default tool types as fallback
const DEFAULT_TOOL_TYPES = [
  "Drill", "Impact Driver", "Circular Saw", "Reciprocating Saw", "Jigsaw",
  "Angle Grinder", "Sander", "Router", "Planer", "Nail Gun",
  "Heat Gun", "Oscillating Tool", "Rotary Hammer", "Table Saw",
  "Miter Saw", "Band Saw", "Chainsaw", "Pressure Washer", "Other"
];

export default function AddTool() {
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const toolPhotoInputRef = useRef(null);
  const toolCameraContainerRef = useRef(null);
  
  const [formData, setFormData] = useState({
    serial_number: "",
    manufacturer: "",
    model_number: "",
    tool_type: "",
    description: "",
    image_url: "",
    estimated_value: "",
    price_source: "",
    scan_price: null,
    scan_price_source: ""
  });
  
  const [scanning, setScanning] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showToolCamera, setShowToolCamera] = useState(false);
  const [serialRescanMode, setSerialRescanMode] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [toolPhotoPreview, setToolPhotoPreview] = useState(null);
  const [labelImageBlob, setLabelImageBlob] = useState(null);
  const [labelImagePreview, setLabelImagePreview] = useState(null);
  const [toolTypes, setToolTypes] = useState(DEFAULT_TOOL_TYPES);
  const [showToolTypeSheet, setShowToolTypeSheet] = useState(false);
  const [focusPoint, setFocusPoint] = useState(null); // For tap-to-focus indicator
  const [showSerialHelp, setShowSerialHelp] = useState(false); // Serial number help dialog
  const [showScanTips, setShowScanTips] = useState(false); // Scan tips popup
  const [showDewaltWarning, setShowDewaltWarning] = useState(false); // DeWalt serial number warning

  // Show scan tips on first visit
  useEffect(() => {
    const hasSeenTips = localStorage.getItem('toolio_scan_tips_seen');
    if (!hasSeenTips) {
      setShowScanTips(true);
    }
  }, []);

  const dismissScanTips = () => {
    setShowScanTips(false);
    localStorage.setItem('toolio_scan_tips_seen', '1');
  };

  // Scroll tool camera into view when opened
  useEffect(() => {
    if (showToolCamera && toolCameraContainerRef.current) {
      // Small delay to allow the camera to mount
      setTimeout(() => {
        toolCameraContainerRef.current?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }, 100);
    }
  }, [showToolCamera]);

  // Fetch tool types from system settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        // Get session token from localStorage for iOS Safari fallback
        const storedToken = localStorage.getItem('session_token');
        const headers = {};
        if (storedToken) {
          headers['Authorization'] = `Bearer ${storedToken}`;
        }
        
        const response = await fetch(`${API}/settings/public`, {
          headers
        });
        if (response.ok) {
          const data = await response.json();
          if (data.tool_types && data.tool_types.length > 0) {
            setToolTypes(data.tool_types);
          }
        }
      } catch (error) {
        // Use default tool types if fetch fails
        console.log("Using default tool types");
      }
    };
    fetchSettings();
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Safety net: if scan detected a tool_type but formData wasn't updated (e.g., Radix Select race)
  useEffect(() => {
    if (scanResult?.tool_type && !formData.tool_type) {
      // Ensure the type is in the list
      setToolTypes(prev => {
        if (prev.includes(scanResult.tool_type)) return prev;
        return [...prev.filter(t => t !== "Other"), scanResult.tool_type, "Other"];
      });
      // Force the form value
      setFormData(prev => ({ ...prev, tool_type: scanResult.tool_type }));
    }
  }, [scanResult, formData.tool_type]);

  // Ref to always hold the latest processImage function (avoids stale closure)
  const processImageRef = useRef(null);

  const captureImage = useCallback(async () => {
    // Manual capture is handled via smartCapture.manualCapture()
    // This is only kept for file upload flow
  }, []);

  // Smart capture callback - receives the best blob
  // Uses ref to always call the latest processImage (not a stale closure)
  const handleSmartCapture = useCallback(async (blob) => {
    setShowCamera(false);
    if (serialRescanMode) {
      // Serial-only rescan — don't overwrite existing fields
      setScanning(true);
      setSerialRescanMode(false);
      try {
        const formData = new FormData();
        formData.append("file", blob, "serial_rescan.jpg");
        const storedToken = localStorage.getItem('session_token');
        const headers = {};
        if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;
        const resp = await fetch(`${API}/ocr/scan-serial`, {
          method: "POST",
          headers,
          credentials: "include",
          body: formData,
        });
        if (resp.ok) {
          const data = await resp.json();
          if (data.serial_number) {
            setFormData(prev => ({ ...prev, serial_number: data.serial_number }));
            toast.success(`Serial number detected: ${data.serial_number}`);
          } else {
            toast.error("Serial number still not detected — please enter it manually");
          }
        } else {
          toast.error("Scan failed — please enter serial number manually");
        }
      } catch {
        toast.error("Scan failed — please enter serial number manually");
      } finally {
        setScanning(false);
      }
    } else {
      setScanning(true);
      await processImageRef.current(blob);
    }
  }, [serialRescanMode]);

  // Smart capture hook - continuous quality analysis + multi-exposure
  const smartCapture = useSmartCapture({
    webcamRef,
    active: showCamera && !showToolCamera,
    onCapture: handleSmartCapture,
  });

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    setScanning(true);
    await processImage(file);
  };

  const processImage = async (imageBlob) => {
    try {
      const uploadData = new FormData();
      uploadData.append("file", imageBlob, "scan.jpg");

      // Get session token from localStorage for iOS Safari fallback
      const storedToken = localStorage.getItem('session_token');
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      const response = await fetch(`${API}/ocr/scan`, {
        method: "POST",
        headers,
        body: uploadData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "OCR scan failed");
      }

      const result = await response.json();
      setScanResult(result);

      // Save the scanned label image for attaching to the tool record
      setLabelImageBlob(imageBlob);
      // Create preview URL
      const previewUrl = URL.createObjectURL(imageBlob instanceof Blob ? imageBlob : new Blob([imageBlob]));
      setLabelImagePreview(previewUrl);
      
      // Populate all detected fields
      const updates = {};
      
      if (result.manufacturer) {
        updates.manufacturer = result.manufacturer;
      }
      if (result.serial_number) {
        updates.serial_number = result.serial_number;
      }
      if (result.model_number) {
        updates.model_number = result.model_number;
      }
      if (result.tool_type) {
        // Always add the type to the list if not present, then select it
        const currentTypes = toolTypes;
        if (!currentTypes.includes(result.tool_type)) {
          // Add it to the list (insert before "Other")
          setToolTypes(prev => {
            if (prev.includes(result.tool_type)) return prev;
            return [...prev.filter(t => t !== "Other"), result.tool_type, "Other"];
          });
        }
        updates.tool_type = result.tool_type;
      }
      if (result.description) {
        updates.description = result.description;
      }
      if (result.estimated_value) {
        updates.estimated_value = result.estimated_value.toString();
        updates.price_source = result.price_source || "Estimated";
        // Lock the scan price — this value persists even if user changes estimated_value
        updates.scan_price = result.estimated_value;
        updates.scan_price_source = result.price_source || "Estimated";
      }
      
      if (Object.keys(updates).length > 0) {
        setFormData(prev => ({ ...prev, ...updates }));
      }

      if (result.manufacturer || result.serial_number || result.model_number) {
        const fieldsDetected = [
          result.manufacturer && "manufacturer",
          result.serial_number && "serial number", 
          result.model_number && "model",
          result.tool_type && "tool type",
          result.description && "description",
          result.estimated_value && "value"
        ].filter(Boolean);
        toast.success(`Detected: ${fieldsDetected.join(", ")}!`);
        // Check for DeWalt with no serial number
        if (result.manufacturer && result.manufacturer.toLowerCase().includes("dewalt") && !result.serial_number) {
          setShowDewaltWarning(true);
        }
        // Auto-recapture if serial number missing
        if (!result.serial_number) {
          toast.warning("Serial number not detected — please focus on the serial number and rescan", { duration: 5000 });
          setTimeout(() => {
            setSerialRescanMode(true);
            setShowCamera(true);
          }, 2000);
        }
      } else {
        toast.info("No fields detected. Please enter manually.");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setScanning(false);
    }
  };

  // Keep the ref updated on every render so handleSmartCapture always calls latest
  processImageRef.current = processImage;

  // Tool photo capture
  const captureToolPhoto = useCallback(async () => {
    if (!webcamRef.current) return;
    
    setUploadingPhoto(true);
    const imageSrc = webcamRef.current.getScreenshot();
    
    if (!imageSrc) {
      toast.error("Failed to capture photo");
      setUploadingPhoto(false);
      return;
    }

    const response = await fetch(imageSrc);
    const blob = await response.blob();
    
    // Create a blob URL for immediate preview
    const blobPreviewUrl = URL.createObjectURL(blob);
    setToolPhotoPreview(blobPreviewUrl);
    
    await uploadToolPhoto(blob, blobPreviewUrl);
    setShowToolCamera(false);
  }, []);

  const handleToolPhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    // Create a local preview immediately using blob URL
    const blobPreviewUrl = URL.createObjectURL(file);
    setToolPhotoPreview(blobPreviewUrl);
    
    setUploadingPhoto(true);
    await uploadToolPhoto(file, blobPreviewUrl);
  };

  const uploadToolPhoto = async (imageBlob, blobPreviewUrl = null) => {
    try {
      const uploadData = new FormData();
      uploadData.append("file", imageBlob, "tool_photo.jpg");

      // Get session token from localStorage for iOS Safari fallback
      const storedToken = localStorage.getItem('session_token');
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      const response = await fetch(`${API}/tools/upload-image`, {
        method: "POST",
        headers,
        body: uploadData
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Upload failed");
      }

      const result = await response.json();
      setFormData(prev => ({ ...prev, image_url: result.image_url }));
      
      // Handle both Cloudinary URLs (https://) and local URLs (/api/uploads/)
      // Keep showing blob preview - it's already displaying correctly
      // The Cloudinary URL will be used when the tool is saved
      const cloudinaryUrl = result.image_url.startsWith('http') 
        ? result.image_url 
        : API.replace('/api', '') + result.image_url;
      
      // Update preview to Cloudinary URL (more permanent)
      setToolPhotoPreview(cloudinaryUrl);
      
      toast.success("Tool photo uploaded!");
    } catch (error) {
      toast.error(error.message);
      setToolPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removeToolPhoto = () => {
    setFormData(prev => ({ ...prev, image_url: "" }));
    setToolPhotoPreview(null);
  };

  // Haptic feedback helper - with iOS fallback using AudioContext
  const triggerHaptic = useCallback((pattern = [50]) => {
    // Try standard Vibration API first (Android, some browsers)
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
        return;
      } catch (e) {
        // Vibration failed, try fallback
      }
    }
    
    // iOS fallback: Create a subtle audio "tick" using AudioContext
    // This creates a very short, low-frequency pulse that can feel like feedback
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        const audioCtx = new AudioContextClass();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        // Very short, low frequency pulse (feels like a tap)
        oscillator.frequency.value = 150;
        oscillator.type = 'sine';
        
        // Quick fade in/out to avoid clicks
        const duration = Math.min(pattern[0] || 50, 30) / 1000;
        gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.1, audioCtx.currentTime + duration / 2);
        gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + duration);
        
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + duration);
        
        // Clean up
        setTimeout(() => audioCtx.close(), 100);
      }
    } catch (e) {
      // Audio feedback not available either, fail silently
    }
  }, []);

  // Tap-to-focus handler for camera
  const handleTapToFocus = async (e, containerRef) => {
    if (!webcamRef.current?.video) return;
    
    const video = webcamRef.current.video;
    const stream = video.srcObject;
    if (!stream) return;
    
    const track = stream.getVideoTracks()[0];
    if (!track) return;
    
    // Get tap position relative to video
    const rect = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    
    // Show focus indicator
    setFocusPoint({ x: e.clientX - rect.left, y: e.clientY - rect.top });
    triggerHaptic([20]); // Quick tap feedback
    
    // Hide focus indicator after animation
    setTimeout(() => setFocusPoint(null), 1000);
    
    // Try to set focus point (advanced constraint)
    try {
      const capabilities = track.getCapabilities();
      
      // Check if device supports focus features
      if (capabilities.focusMode) {
        const constraints = { advanced: [{ focusMode: "manual" }] };
        
        // If device supports focus distance, try to set it
        if (capabilities.focusDistance) {
          // Set focus to closer range for serial number scanning
          const minFocus = capabilities.focusDistance.min || 0;
          const maxFocus = capabilities.focusDistance.max || 1;
          // Focus closer (lower values = closer focus)
          constraints.advanced[0].focusDistance = minFocus + (maxFocus - minFocus) * 0.2;
        }
        
        // If device supports point of interest
        if (capabilities.pointsOfInterest) {
          constraints.advanced[0].pointsOfInterest = [{ x, y }];
        }
        
        await track.applyConstraints(constraints);
        
        // Return to continuous autofocus after a short delay
        setTimeout(async () => {
          try {
            await track.applyConstraints({ advanced: [{ focusMode: "continuous" }] });
          } catch (e) {
            // Ignore - continuous focus might not be supported
          }
        }, 2000);
      }
    } catch (error) {
      // Focus control not supported on this device - silently ignore
      console.log("Focus control not available:", error.message);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!formData.serial_number || !formData.manufacturer || !formData.tool_type) {
      triggerHaptic([100, 50, 100]); // Error pattern
      toast.error("Please fill in all required fields");
      return;
    }

    if (!formData.image_url) {
      triggerHaptic([100, 50, 100]);
      toast.error("Please add a photo of your tool");
      // Scroll to photo section
      document.getElementById('tool-photo-section')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);

    try {
      // Upload label scan image if we have one
      let labelImageUrl = null;
      if (labelImageBlob) {
        try {
          const labelFormData = new FormData();
          labelFormData.append("file", labelImageBlob, "label-scan.jpg");
          const storedToken = localStorage.getItem('session_token');
          const uploadHeaders = {};
          if (storedToken) uploadHeaders['Authorization'] = `Bearer ${storedToken}`;
          const uploadResp = await fetch(`${API}/tools/upload-image`, {
            method: "POST",
            headers: uploadHeaders,
            body: labelFormData
          });
          if (uploadResp.ok) {
            const uploadData = await uploadResp.json();
            labelImageUrl = uploadData.image_url;
          }
        } catch (err) {
          console.warn("Label image upload failed:", err);
        }
      }

      // Image priority logic:
      // - User added a tool photo → that's primary (image_url), label is secondary (label_image_url)
      // - No tool photo → label becomes primary (image_url), no secondary
      const userPhoto = formData.image_url;
      const finalImageUrl = userPhoto || labelImageUrl || "";
      const finalLabelUrl = userPhoto ? labelImageUrl : null;

      // Prepare data with proper types
      const submitData = {
        ...formData,
        image_url: finalImageUrl,
        label_image_url: finalLabelUrl,
        estimated_value: formData.estimated_value ? parseFloat(formData.estimated_value) : null,
        scan_price: formData.scan_price ? parseFloat(formData.scan_price) : null,
      };
      
      // Get session token from localStorage for iOS Safari fallback
      const storedToken = localStorage.getItem('session_token');
      const headers = { "Content-Type": "application/json" };
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      const response = await fetch(`${API}/tools`, {
        method: "POST",
        headers,
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || "Failed to add tool");
      }

      triggerHaptic([20, 30, 20, 30, 50]); // Success celebration pattern

      // Confetti celebration
      const burst = (opts) => confetti({ ...opts, disableForReducedMotion: true });
      burst({ particleCount: 80, spread: 70, origin: { y: 0.6 } });
      setTimeout(() => burst({ particleCount: 50, angle: 60, spread: 55, origin: { x: 0 } }), 250);
      setTimeout(() => burst({ particleCount: 50, angle: 120, spread: 55, origin: { x: 1 } }), 400);

      toast.success("Tool added successfully!");
      setTimeout(() => navigate("/dashboard"), 900);
    } catch (error) {
      triggerHaptic([100, 50, 100]); // Error pattern
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center gap-4 px-4 py-3 lg:px-8">
          <Link to="/dashboard">
            <Button variant="ghost" size="sm" className="h-10 md:h-8" data-testid="back-btn">
              <ArrowLeft className="w-5 h-5 md:w-4 md:h-4 mr-1 md:mr-2" />
              <span className="hidden md:inline">Back</span>
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 md:w-6 md:h-6 text-primary" />
            <span className="font-heading text-base md:text-lg font-bold uppercase">Add New Tool</span>
          </div>
          <Link to="/" className="md:hidden ml-auto" data-testid="addtool-header-logo-link">
            <img src="/toolio-logo-new.png" alt="TOOLIO" className="h-8" />
          </Link>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-3xl mx-auto space-y-4 md:space-y-6">
        {/* Scan Label Section */}
        <Card className="border-border bg-card relative overflow-hidden">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowScanTips(true)}
            className="absolute top-0 right-0 rounded-none rounded-bl-lg bg-primary/10 hover:bg-primary/20 text-primary text-xs px-2 py-0.5 h-auto z-10"
            data-testid="open-scan-tips-btn"
          >
            <HelpCircle className="w-3 h-3 mr-1" />
            Scanning tips
          </Button>
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="font-heading uppercase text-xl md:text-2xl flex items-baseline gap-3">
              <span className="text-3xl md:text-4xl font-black text-primary">1.</span>
              Scan Tool Label
            </CardTitle>
            <CardDescription className="text-base md:text-base">
              Use your camera to scan or upload an image of your tool label and we'll auto-fill the tool details below
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {showCamera && !showToolCamera ? (
              <div className="space-y-4">
                {/* Scan guidance - above viewfinder */}
                <div className="py-2" data-testid="scan-guidance">
                  {serialRescanMode ? (
                    <div className="flex gap-3 items-start" data-testid="serial-rescan-banner">
                      <Search className="w-7 h-7 text-amber-400 shrink-0 mt-2" />
                      <div>
                        <p className="text-amber-400 text-base font-bold mb-1">Serial Number Rescan</p>
                        <p className="text-amber-400 text-base font-semibold">We had trouble reading the serial number. Please try again — hold your device approx 20cm away and wait for the overlay to turn green before capturing</p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-primary text-base font-semibold text-center">Hold your device approx 20cm away from the label. No need to fill the viewfinder.</p>
                  )}
                </div>
                {/* Camera View Container - landscape oriented for tool labels */}
                <div 
                  className="relative w-full overflow-hidden rounded-xl md:rounded border border-border cursor-pointer" 
                  style={{ height: '35vh', maxHeight: '35vh' }}
                  onClick={(e) => handleTapToFocus(e)}
                >
                  <Webcam
                    ref={webcamRef}
                    audio={false}
                    screenshotFormat="image/jpeg"
                    screenshotQuality={0.92}
                    videoConstraints={VIDEO_CONSTRAINTS}
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{ transform: `scale(${smartCapture.zoom})`, transformOrigin: 'center center' }}
                  />
                  {/* Camera overlay with quality-reactive frame */}
                  <div className="camera-overlay">
                    <div className="camera-frame" data-quality={smartCapture.quality}>
                      <div className="camera-corner camera-corner-tl" />
                      <div className="camera-corner camera-corner-tr" />
                      <div className="camera-corner camera-corner-bl" />
                      <div className="camera-corner camera-corner-br" />
                      {smartCapture.capturing && (
                        <div className="absolute left-0 right-0 h-0.5 bg-primary scan-line" />
                      )}
                    </div>
                  </div>
                  {/* Zoom indicator */}
                  {smartCapture.zoom > 1 && (
                    <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full pointer-events-none">
                      {smartCapture.zoom.toFixed(1)}x
                    </div>
                  )}
                  {/* Tap to focus indicator */}
                  {focusPoint && (
                    <div 
                      className="absolute w-16 h-16 border-2 border-yellow-400 rounded-lg pointer-events-none animate-pulse"
                      style={{ 
                        left: focusPoint.x - 32, 
                        top: focusPoint.y - 32,
                        boxShadow: '0 0 20px rgba(250, 204, 21, 0.5)'
                      }}
                    >
                      <Focus className="w-6 h-6 text-yellow-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                    </div>
                  )}
                  {/* Quality hint */}
                  <div className={`absolute bottom-2 left-1/2 -translate-x-1/2 text-white text-xs px-3 py-1 rounded-full pointer-events-none transition-colors duration-300 ${
                    smartCapture.quality === 'green' ? 'bg-green-600/80' : 
                    smartCapture.quality === 'yellow' ? 'bg-yellow-600/80' : 'bg-red-600/80'
                  }`}>
                    {smartCapture.capturing ? 'Capturing...' :
                     smartCapture.hint === 'ready' ? 'Ready - press capture' :
                     smartCapture.hint === 'move-back' ? 'Move back slightly' :
                     smartCapture.hint === 'checking' ? 'Checking label...' :
                     'Align label - adjust lighting'}
                  </div>
                </div>
                
                {/* Buttons - always visible below camera */}
                <div className="flex gap-2">
                  <Button 
                    onClick={smartCapture.manualCapture} 
                    className="flex-1 btn-primary h-14 md:h-10 text-base md:text-sm"
                    disabled={scanning || smartCapture.capturing}
                    data-testid="capture-btn"
                  >
                    {smartCapture.capturing ? (
                      <><Loader2 className="w-5 h-5 md:w-4 md:h-4 mr-2 animate-spin" /> Capturing...</>
                    ) : (
                      <><ScanLine className="w-5 h-5 md:w-4 md:h-4 mr-2" /> Capture Now</>
                    )}
                  </Button>
                  <Button 
                    variant="outline" 
                    onClick={() => { setShowCamera(false); setSerialRescanMode(false); }}
                    className="h-14 md:h-10 border-secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Processing Overlay - Shows when scanning after camera closes */}
                {scanning ? (
                  <div className="bg-primary/10 border-2 border-primary/50 rounded-xl p-8 text-center">
                    <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
                    <p className="text-lg font-bold text-primary">Analysing Tool Label...</p>
                    <p className="text-sm text-muted-foreground mt-2">Our AI is reading the label details</p>
                    <p className="text-xs text-yellow-400 mt-3">This can take up to 12 seconds</p>
                  </div>
                ) : (
                  <>
                    {/* Scan Button - Prominent */}
                    <Button
                      className="w-full h-16 btn-primary flex items-center justify-center gap-2 rounded-xl text-base font-bold"
                      onClick={() => { setShowCamera(true); setShowToolCamera(false); }}
                      disabled={scanning}
                      data-testid="open-camera-btn"
                    >
                      <Camera className="w-6 h-6" />
                      <span>Scan Tool Label</span>
                    </Button>
                    
                    {/* Upload - Secondary */}
                    <Button
                      variant="ghost"
                      className="w-full h-8 text-muted-foreground hover:text-yellow-400 text-sm"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={scanning}
                      data-testid="upload-image-btn"
                    >
                      <Upload className="w-4 h-4 mr-2" /> Or upload an image
                    </Button>
                  </>
                )}
                
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </div>
            )}

            {/* Scan Result */}
            {scanResult && (
              <Card className="border-primary/50 bg-primary/5 rounded-xl">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    {(scanResult.manufacturer || scanResult.serial_number) ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-yellow-500" />
                    )}
                    <span className="font-heading uppercase text-base md:text-sm">Scan Results</span>
                  </div>
                  <div className="space-y-2 text-base md:text-sm">
                    <p><span className="text-muted-foreground">Manufacturer:</span> <span className="font-mono">{scanResult.manufacturer || "Not detected"}</span></p>
                    <p><span className="text-muted-foreground">Model Number:</span> <span className="font-mono">{scanResult.model_number || "Not detected"}</span></p>
                    <p><span className="text-muted-foreground">Serial Number:</span> <span className="font-mono">{scanResult.serial_number || "Not detected"}</span></p>
                    <p><span className="text-muted-foreground">Tool Type:</span> <span className="font-mono">{scanResult.tool_type || "Not detected"}</span></p>
                    {scanResult.description && (
                      <p><span className="text-muted-foreground">Description:</span> <span className="font-mono">{scanResult.description}</span></p>
                    )}
                    {scanResult.estimated_value && (
                      <p><span className="text-muted-foreground">Est. Value:</span> <span className="font-mono">£{scanResult.estimated_value}</span></p>
                    )}
                  </div>
                  <div className="mt-4 pt-3 border-t border-primary/20">
                    <p className="text-sm text-muted-foreground leading-normal">⚠️ Please verify the <strong className="text-primary">Manufacturer</strong> and <strong className="text-primary">Serial Number</strong> are correct - these are used to identify your tool. If incorrect, <span className="text-primary underline cursor-pointer hover:text-primary/80" onClick={() => setScanResult(null)}>re-scan your tool label</span> or edit the fields below manually.</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </CardContent>
        </Card>

        {/* Tool Details Form */}
        <Card className="border-border bg-card relative overflow-hidden">
          {/* Help Button - Top Right Corner */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowSerialHelp(true)}
            className="absolute top-0 right-0 rounded-none rounded-bl-lg bg-primary/10 hover:bg-primary/20 text-primary text-xs px-2 py-0.5 h-auto z-10"
          >
            <HelpCircle className="w-3 h-3 mr-1" />
            Help finding my serial number
          </Button>
          
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="font-heading uppercase text-xl md:text-2xl flex items-baseline gap-3">
              <span className="text-3xl md:text-4xl font-black text-primary">2.</span>
              Tool Details
            </CardTitle>
            <CardDescription className="text-base md:text-base">
              Manually add your tool details or complete a partial scan. <span className="italic text-sm">Fields marked with * are required</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="manufacturer" className="text-base md:text-sm">Manufacturer *</Label>
                  <Input
                    id="manufacturer"
                    value={formData.manufacturer}
                    onChange={(e) => handleInputChange("manufacturer", e.target.value)}
                    placeholder="e.g., DEWALT"
                    className="bg-background border-input uppercase mobile-input md:h-10 text-base md:text-sm"
                    required
                    data-testid="manufacturer-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="serial_number" className="text-base md:text-sm">Serial Number *</Label>
                  <Input
                    id="serial_number"
                    value={formData.serial_number}
                    onChange={(e) => handleInputChange("serial_number", e.target.value)}
                    placeholder="e.g., ABC123456"
                    className="bg-background border-input font-mono mobile-input md:h-10 text-base md:text-sm"
                    required
                    data-testid="serial-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model_number" className="text-base md:text-sm">Model Number</Label>
                  <Input
                    id="model_number"
                    value={formData.model_number}
                    onChange={(e) => handleInputChange("model_number", e.target.value)}
                    placeholder="e.g., DCD920D2"
                    className="bg-background border-input font-mono mobile-input md:h-10 text-base md:text-sm"
                    data-testid="model-number-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tool_type" className="text-base md:text-sm">Tool Type *</Label>
                  
                  {/* Desktop: Use standard Select */}
                  <div className="hidden md:block">
                    <Select 
                      value={formData.tool_type} 
                      onValueChange={(value) => handleInputChange("tool_type", value)}
                    >
                      <SelectTrigger className="bg-background border-input h-10" data-testid="tool-type-select">
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        {toolTypes.filter(type => type !== "Other").map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                        {toolTypes.includes("Other") && (
                          <SelectItem value="Other">Other</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Mobile: Use bottom sheet for better scrolling */}
                  <div className="md:hidden">
                    <button
                      type="button"
                      onClick={() => setShowToolTypeSheet(true)}
                      className="w-full h-14 px-3 flex items-center justify-between bg-background border border-input rounded-md text-left"
                      data-testid="tool-type-select-mobile"
                    >
                      <span className={formData.tool_type ? "text-foreground" : "text-muted-foreground"}>
                        {formData.tool_type || "Select type"}
                      </span>
                      <ChevronDown className="w-5 h-5 text-muted-foreground" />
                    </button>
                    
                    <Sheet open={showToolTypeSheet} onOpenChange={setShowToolTypeSheet}>
                      <SheetContent side="bottom" className="h-[70vh] rounded-t-2xl">
                        <SheetHeader>
                          <SheetTitle>Select Tool Type</SheetTitle>
                        </SheetHeader>
                        <div className="overflow-y-auto h-full pb-10 mt-4">
                          <div className="space-y-1">
                            {/* Show all types except "Other" first */}
                            {toolTypes.filter(type => type !== "Other").map(type => (
                              <button
                                key={type}
                                type="button"
                                onClick={() => {
                                  handleInputChange("tool_type", type);
                                  setShowToolTypeSheet(false);
                                }}
                                className={`w-full p-4 flex items-center justify-between rounded-lg text-left transition-colors ${
                                  formData.tool_type === type 
                                    ? "bg-primary/20 text-primary" 
                                    : "hover:bg-secondary/50 active:bg-secondary"
                                }`}
                              >
                                <span className="text-base">{type}</span>
                                {formData.tool_type === type && (
                                  <Check className="w-5 h-5 text-primary" />
                                )}
                              </button>
                            ))}
                            {/* "Other" always at the bottom */}
                            {toolTypes.includes("Other") && (
                              <>
                                <div className="border-t border-border my-2" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleInputChange("tool_type", "Other");
                                    setShowToolTypeSheet(false);
                                  }}
                                  className={`w-full p-4 flex items-center justify-between rounded-lg text-left transition-colors ${
                                    formData.tool_type === "Other" 
                                      ? "bg-primary/20 text-primary" 
                                      : "hover:bg-secondary/50 active:bg-secondary"
                                  }`}
                                >
                                  <span className="text-base">Other</span>
                                  {formData.tool_type === "Other" && (
                                    <Check className="w-5 h-5 text-primary" />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </SheetContent>
                    </Sheet>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description" className="text-base md:text-sm">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  placeholder="Additional notes about this tool..."
                  className="bg-background border-input min-h-[100px] text-base md:text-sm"
                  rows={3}
                  data-testid="description-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Label htmlFor="estimated-value" className="text-base md:text-sm">Replacement Value £</Label>
                  {formData.price_source && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      formData.price_source === 'Retail' 
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                        : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                    }`} data-testid="price-source-badge">
                      {formData.price_source === 'Retail' ? 'Retail' : 'Estimated'}
                    </span>
                  )}
                </div>
                <Input
                  id="estimated-value"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.estimated_value}
                  onChange={(e) => handleInputChange("estimated_value", e.target.value)}
                  placeholder="e.g., 299.99"
                  className="bg-background border-input h-14 md:h-10 text-base md:text-sm"
                  data-testid="estimated-value-input"
                />
                <p className="text-sm md:text-xs text-muted-foreground">
                  {formData.price_source === 'Retail' 
                    ? 'Live retail price sourced at time of scanning. Adjust if needed.' 
                    : 'For insurance purposes. Having a value recorded helps speed up claims if your tool is ever stolen.'}
                </p>
                {/* Show locked scan price if user has changed the value */}
                {formData.scan_price && formData.estimated_value && 
                  parseFloat(formData.estimated_value) !== formData.scan_price && (
                  <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/30 rounded px-3 py-2 border border-border" data-testid="scan-price-locked">
                    <span>TOOLIO scan price:</span>
                    <span className="font-mono font-medium text-foreground">£{formData.scan_price.toFixed(2)}</span>
                    <span className={`px-1.5 py-0.5 rounded-full ${
                      formData.scan_price_source === 'Retail' 
                        ? 'bg-green-500/20 text-green-400' 
                        : 'bg-yellow-500/20 text-yellow-400'
                    }`}>{formData.scan_price_source}</span>
                    <span className="ml-auto opacity-60">locked</span>
                  </div>
                )}
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Tool Photo Section - Card 3 */}
        <Card className="border-border bg-card">
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="font-heading uppercase text-xl md:text-2xl flex items-baseline gap-3">
              <span className="text-3xl md:text-4xl font-black text-primary">3.</span>
              Tool Photo
            </CardTitle>
            <CardDescription className="text-base md:text-base">
              Add a photo of your tool for easy identification in your inventory, by the police and other TOOLIO users.
              {labelImagePreview && " Your scanned label will also be saved."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" id="tool-photo-section">
              {/* Label Scan Image Preview */}
              {labelImagePreview && (
                  <div className="mb-3 p-3 rounded-lg border border-primary/20 bg-primary/5" data-testid="label-image-preview">
                    <div className="flex items-center gap-3">
                      <div className="w-16 h-16 rounded border border-primary/30 overflow-hidden flex-shrink-0">
                        <img src={labelImagePreview} alt="Scanned label" className="w-full h-full object-cover" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-primary">Label scan captured</p>
                        <p className="text-xs text-muted-foreground">This will be saved with your tool as proof of ownership</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {showToolCamera ? (
                  <div className="space-y-4" ref={toolCameraContainerRef}>
                    {/* Camera View - constrained height on mobile */}
                    <div 
                      className="relative w-full overflow-hidden rounded-xl md:rounded border border-border cursor-pointer" 
                      style={{ height: '55vh', maxHeight: '55vh' }}
                      onClick={(e) => handleTapToFocus(e)}
                    >
                      <Webcam
                        ref={webcamRef}
                        audio={false}
                        screenshotFormat="image/jpeg"
                        screenshotQuality={0.92}
                        videoConstraints={VIDEO_CONSTRAINTS}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      {/* Tap to focus indicator */}
                      {focusPoint && (
                        <div 
                          className="absolute w-16 h-16 border-2 border-yellow-400 rounded-lg pointer-events-none animate-pulse"
                          style={{ 
                            left: focusPoint.x - 32, 
                            top: focusPoint.y - 32,
                            boxShadow: '0 0 20px rgba(250, 204, 21, 0.5)'
                          }}
                        >
                          <Focus className="w-6 h-6 text-yellow-400 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
                        </div>
                      )}
                      {/* Tap to focus hint */}
                      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white text-xs px-3 py-1 rounded-full pointer-events-none">
                        Tap to focus
                      </div>
                    </div>
                    
                    {/* Buttons - always visible below camera */}
                    <div className="flex gap-2">
                      <Button 
                        type="button"
                        onClick={captureToolPhoto} 
                        className="flex-1 btn-primary h-14 md:h-10"
                        disabled={uploadingPhoto}
                        data-testid="capture-tool-photo-btn"
                      >
                        {uploadingPhoto ? (
                          <><Loader2 className="w-5 h-5 md:w-4 md:h-4 mr-2 animate-spin" /> Uploading...</>
                        ) : (
                          <><Camera className="w-5 h-5 md:w-4 md:h-4 mr-2" /> Capture Photo</>
                        )}
                      </Button>
                      <Button 
                        type="button"
                        variant="outline" 
                        onClick={() => setShowToolCamera(false)}
                        className="h-14 md:h-10 border-secondary"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : toolPhotoPreview ? (
                  <div className="relative">
                    <img 
                      src={toolPhotoPreview} 
                      alt="Tool preview" 
                      className="w-full max-h-48 object-contain rounded-xl md:rounded border border-border bg-black/20"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 h-10 md:h-8"
                      onClick={removeToolPhoto}
                      data-testid="remove-photo-btn"
                    >
                      <X className="w-4 h-4 mr-1" /> Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Take Photo - Prominent */}
                    <Button
                      type="button"
                      className="w-full h-16 btn-primary flex items-center justify-center gap-2 rounded-xl text-base font-bold"
                      onClick={() => { setShowToolCamera(true); setShowCamera(false); }}
                      disabled={uploadingPhoto}
                      data-testid="open-tool-camera-btn"
                    >
                      <Camera className="w-6 h-6" />
                      <span>Take Photo of Tool</span>
                    </Button>
                    
                    {/* Upload - Secondary */}
                    <Button
                      type="button"
                      variant="ghost"
                      className="w-full h-8 text-muted-foreground hover:text-yellow-400 text-sm"
                      onClick={() => toolPhotoInputRef.current?.click()}
                      disabled={uploadingPhoto}
                      data-testid="upload-tool-photo-btn"
                    >
                      {uploadingPhoto ? (
                        <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                      ) : (
                        <><ImagePlus className="w-4 h-4 mr-2" /> Or upload a photo</>
                      )}
                    </Button>
                    <input
                      ref={toolPhotoInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleToolPhotoUpload}
                      className="hidden"
                    />
                  </div>
                )}
              </div>
          </CardContent>
        </Card>

        {/* Submit Button */}
        <Button 
          onClick={handleSubmit}
          className="w-full h-14 md:h-10 text-base md:text-sm bg-green-600 hover:bg-green-700 text-white font-bold" 
          disabled={submitting}
          data-testid="submit-tool-btn"
        >
          {submitting ? (
            <><Loader2 className="w-5 h-5 md:w-4 md:h-4 mr-2 animate-spin" /> Adding Tool...</>
          ) : (
            <><CheckCircle className="w-5 h-5 md:w-4 md:h-4 mr-2" /> Add Tool to Inventory</>
          )}
        </Button>
      </main>

      {/* DeWalt Serial Number Warning Dialog */}
      <Dialog open={showDewaltWarning} onOpenChange={setShowDewaltWarning}>
        <DialogContent className="max-w-sm p-0 overflow-hidden rounded-xl flex flex-col" style={{ maxHeight: 'min(75vh, 75dvh)' }} data-testid="dewalt-warning-dialog">
          <div className="bg-[#febd17] px-4 py-4 flex items-center gap-3 flex-shrink-0">
            <img
              src="https://res.cloudinary.com/dzx4iay2g/image/upload/v1774575010/toolio/dewalt/iclbx1dmya0ugh3tdjka.jpg"
              alt="DeWalt logo"
              className="h-12 w-auto"
            />
            <div>
              <DialogTitle className="text-black font-heading uppercase text-base">DeWalt Serial Number</DialogTitle>
              <p className="text-black/70 text-sm">Brand-specific scanning issue</p>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              Some DeWalt tools <strong className="text-foreground">do not include the needed serial number on the main rating label</strong>. Instead the number is engraved either:
            </p>
            <ul className="text-sm text-muted-foreground leading-relaxed list-disc pl-5 space-y-1">
              <li>Inside the battery compartment</li>
              <li>Near the motor housing</li>
              <li>On the tool base on certain corded or stationary models</li>
            </ul>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Do not confuse the Serial Number with the Date Code, it is the <strong className="text-foreground">engraved Serial Number</strong> that uniquely identifies your tool.
            </p>
            <div className="rounded-lg overflow-hidden border border-border">
              <img
              src="https://res.cloudinary.com/dzx4iay2g/image/upload/v1774443669/toolio/dewalt/jyadkfqeawqy0lm2whlz.jpg"
                alt="DeWalt serial number location under battery compartment"
                className="w-full h-52 object-contain bg-black/20"
              />
              <p className="text-xs text-muted-foreground p-2 text-center italic">Date code and serial number inside the battery compartment</p>
            </div>
            <div className="bg-[#febd17]/10 border border-[#febd17]/30 rounded-lg p-3">
              <p className="text-sm leading-relaxed">
                <strong className="text-foreground">Please enter the serial number manually</strong> in the form below. Check under the battery or on the base of the tool.
              </p>
            </div>
          </div>
          <div className="px-4 pb-4 pt-2 border-t border-border flex-shrink-0">
            <Button
              className="w-full h-10 font-bold text-sm"
              style={{ backgroundColor: '#febd17', color: '#000' }}
              onClick={() => setShowDewaltWarning(false)}
              data-testid="dewalt-warning-ok-btn"
            >
              Got it, I'll enter it manually
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Scan Tips Dialog - shows on first visit */}
      <Dialog open={showScanTips} onOpenChange={setShowScanTips}>
        <DialogContent className="max-w-sm p-0 overflow-hidden rounded-xl flex flex-col" style={{ maxHeight: 'min(82vh, 82dvh)' }} data-testid="scan-tips-dialog">
          <DialogHeader className="p-4 pb-2 flex-shrink-0">
            <DialogTitle className="font-heading uppercase text-xl flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              Scanning Tips
            </DialogTitle>
            <p className="text-base text-muted-foreground">
              Get the best results from your tool label scans
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-4 pb-3">
            <div className="space-y-3">

              {/* Tip 1: Clean the label */}
              <div className="rounded-lg overflow-hidden border border-border">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/2a8be6fd-9c5a-4960-8711-464ee98935b9/images/3e0b6bd08601555c6665cbb3c43f4a6f0254313d189461a5f6e9131e69d9c311.png"
                  alt="Clean vs dirty label comparison"
                  className="w-full h-24 object-cover"
                  loading="lazy"
                />
                <div className="p-3 bg-card">
                  <h3 className="font-heading text-lg font-bold text-primary mb-1">1. Clean the Label First</h3>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    Wipe off dust, grease or dirt. A clean label means a much more accurate scan.
                  </p>
                </div>
              </div>

              {/* Tip 2: Good lighting */}
              <div className="rounded-lg overflow-hidden border border-border">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/2a8be6fd-9c5a-4960-8711-464ee98935b9/images/f683e5e1b73713ab4b616da811b1c2adf2391eb56b30e206d1b4411194445624.png"
                  alt="Good lighting for scanning"
                  className="w-full h-24 object-cover"
                  loading="lazy"
                />
                <div className="p-3 bg-card">
                  <h3 className="font-heading text-lg font-bold text-primary mb-1">2. Use Good, Even Lighting</h3>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    Avoid shadows and glare. Natural daylight or a well-lit room works best.
                  </p>
                </div>
              </div>

              {/* Tip 3: Camera angle */}
              <div className="rounded-lg overflow-hidden border border-border">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/2a8be6fd-9c5a-4960-8711-464ee98935b9/images/8c0bba6c810200a8bca929a275fa423d495f26e184824c54b115ecacfcdec459.png"
                  alt="Hold camera directly above the label"
                  className="w-full h-24 object-cover"
                  loading="lazy"
                />
                <div className="p-3 bg-card">
                  <h3 className="font-heading text-lg font-bold text-primary mb-1">3. Hold Your Phone Directly Above</h3>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    Place the tool on a flat surface to reduce movement. Point the camera straight down and wait for the focus indicator to turn green.
                  </p>
                </div>
              </div>

              {/* Tip 4: Verify results */}
              <div className="rounded-lg overflow-hidden border border-border">
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/2a8be6fd-9c5a-4960-8711-464ee98935b9/images/735fc648e7402cc1d75a95966ad5acee5b80bfbdbc5135c60d44e5e61918db73.png"
                  alt="Verify scan results"
                  className="w-full h-24 object-cover"
                  loading="lazy"
                />
                <div className="p-3 bg-card">
                  <h3 className="font-heading text-lg font-bold text-primary mb-1">4. Check the Manufacturer & Serial Number</h3>
                  <p className="text-base text-muted-foreground leading-relaxed">
                    After every scan, <strong className="text-primary">always check the Manufacturer and Serial Number are correct</strong>. If wrong, re-scan or edit manually.
                  </p>
                </div>
              </div>

              {/* Extra tips */}
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                <h3 className="font-heading text-lg font-bold mb-1.5">Quick Tips</h3>
                <ul className="text-base text-muted-foreground space-y-1.5 leading-relaxed">
                  <li className="flex gap-2"><span className="text-primary font-bold">-</span> Tap the screen to focus on a specific area</li>
                  <li className="flex gap-2"><span className="text-primary font-bold">-</span> If text is faint, try chalk or Tipp-Ex over engraved text</li>
                  <li className="flex gap-2"><span className="text-primary font-bold">-</span> You can upload a photo instead of using the live camera</li>
                  <li className="flex gap-2"><span className="text-primary font-bold">-</span> Check inside battery compartments for hidden labels</li>
                </ul>
              </div>

            </div>
          </div>
          <div className="px-4 pb-4 pt-2 border-t border-border flex-shrink-0">
            <Button
              className="w-full h-10 btn-primary font-bold text-base"
              onClick={dismissScanTips}
              data-testid="scan-tips-got-it-btn"
            >
              Got it, let's scan!
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Serial Number Help Dialog */}
      <Dialog open={showSerialHelp} onOpenChange={setShowSerialHelp}>
        <DialogContent className="max-w-lg p-0 overflow-hidden rounded-xl flex flex-col" style={{ maxHeight: 'min(80vh, 80dvh)' }}>
          <DialogHeader className="p-5 pb-3 flex-shrink-0 pt-8">
            <DialogTitle className="font-heading uppercase text-xl">Where to Find Your Serial Number</DialogTitle>
            <p className="text-base text-muted-foreground">
              Serial numbers are usually found on a label or engraved plate on your tool. Check inside battery compartments too.
            </p>
            <p className="text-base text-muted-foreground mt-2">
              <strong className="text-yellow-400">Tip:</strong> If the serial is engraved and hard to read, try rubbing a little chalk, Tipp-Ex or a finger dab of paint over it, then wipe off the excess - this makes the numbers stand out.
            </p>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-5 pb-5">
            <div className="space-y-6">
              
              {/* Bosch */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Bosch</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Look for a silver or white label on the main body of the tool, usually near the motor housing or on the bottom. The serial number is typically a 10-digit number starting with "0" or "3".
                </p>
                <div className="text-base text-foreground italic">Common locations: Bottom of tool, near the air vents, or on the battery compartment.</div>
              </div>

              {/* DeWalt */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">DeWalt</h3>
                <p className="text-base text-muted-foreground mb-2">
                  DeWalt serial numbers are found on a yellow and black label, typically on the motor housing or handle. The format is usually 8-10 alphanumeric characters.
                </p>
                <div className="text-base text-foreground italic">Common locations: Side of the motor housing, bottom of the handle, or near / under the battery slot.</div>
              </div>

              {/* Festool */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Festool</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Festool tools have a data plate with serial number, usually on the underside or rear of the tool. Look for "S/N" or "Serial No." followed by numbers.
                </p>
                <div className="text-base text-foreground italic">Common locations: Underside of tool, rear housing, or inside the battery compartment.</div>
              </div>

              {/* Hikoki (formerly Hitachi) */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Hikoki / Hitachi</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Serial numbers are on a silver label on the main body. Older Hitachi tools may have the number engraved. Format is typically alphanumeric, 8-12 characters.
                </p>
                <div className="text-base text-foreground italic">Common locations: Side of motor housing, near the cord entry point, or on the base plate.</div>
              </div>

              {/* Makita */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Makita</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Makita serial numbers are on a white or silver label, usually on the side of the tool body near the motor. The serial is typically 6-8 digits, sometimes with a letter prefix.
                </p>
                <div className="text-base text-foreground italic">Common locations: Side of motor housing, under the battery, or near the air intake.</div>
              </div>

              {/* Milwaukee */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Milwaukee</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Look for a white label with barcode on the tool body. The serial number is below the barcode, typically starting with letters followed by numbers (e.g., "A12B123456").
                </p>
                <div className="text-base text-foreground italic">Common locations: Main body near the trigger, bottom of handle, or inside the battery compartment.</div>
              </div>

              {/* Metabo */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Metabo</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Metabo places serial numbers on a rating plate, usually on the motor housing. The number is typically 8-10 digits and may include manufacturing date codes.
                </p>
                <div className="text-base text-foreground italic">Common locations: Side of the tool body, near the gear housing, or on the base.</div>
              </div>

              {/* Paslode */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Paslode</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Paslode nail guns have serial numbers engraved or on a label on the main body. Look near the magazine or on the side of the combustion chamber housing.
                </p>
                <div className="text-base text-foreground italic">Common locations: Side of the body, near the fuel cell compartment, or on the handle.</div>
              </div>

              {/* Ryobi */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Ryobi</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Ryobi serial numbers are on a green or white label on the tool body. The format varies but is usually 10-14 characters including letters and numbers.
                </p>
                <div className="text-base text-foreground italic">Common locations: Bottom of the tool, side of motor housing, or near the battery connection.</div>
              </div>

              {/* Snap-on */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Snap-on</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Snap-on tools typically have serial numbers engraved on the tool itself or on a small metal plate. Air tools often have the number near the air inlet.
                </p>
                <div className="text-base text-foreground italic">Common locations: Engraved on the body, on a riveted plate, or near the drive mechanism.</div>
              </div>

              {/* Stihl */}
              <div className="border-b border-border pb-4">
                <h3 className="font-heading text-lg font-bold text-primary mb-2">Stihl</h3>
                <p className="text-base text-muted-foreground mb-2">
                  Stihl serial numbers are on a silver plate on the crankcase or motor housing. Chainsaws have it near the rear handle. Format is typically 9 digits.
                </p>
                <div className="text-base text-foreground italic">Common locations: Side of the engine/motor, near the starter, or on the crankcase.</div>
              </div>

              {/* Can't find it? */}
              <div className="bg-muted/50 rounded-lg p-4">
                <h3 className="font-heading text-lg font-bold mb-2">Can't Find Your Serial Number?</h3>
                <ul className="text-base text-muted-foreground space-y-1">
                  <li>• Check the original box or receipt</li>
                  <li>• Look inside battery compartments</li>
                  <li>• Check under removable covers or plates</li>
                  <li>• Some tools have it engraved rather than on a label</li>
                  <li>• Contact the manufacturer with your model number</li>
                </ul>
              </div>

            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
